import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  AccountDetailForSelector,
  InlineAlertType,
} from '@varo/graphql-schema-types';
import { format, parseISO } from 'date-fns';
import {
  getAvailableBalance,
  getCheckingAccount,
  getSavingAccount,
} from 'features/lineOfCredit/helpers/varoLineOfCreditManualRepay.helpers';
import useFetchLineOfCreditPaymentDetails from 'features/lineOfCredit/screens/paymentDetails/hook/useFetchLineOfCreditPaymentDetails';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { applyKeyToAmount, parseToFloat } from 'shared/utils/numberUtils';
import { formatCurrency } from 'shared/utils/string/currency';

interface PaymentAlert {
  message: string;
  type: InlineAlertType;
}

const useRepayVaroLineOfCredit = () => {
  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'repaymentDetails',
  });
  const { serializedData, fetching, error } =
    useFetchLineOfCreditPaymentDetails();

  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const paymentAccountOptions = useMemo(() => {
    return serializedData?.fromAccountSelectorView ?? [];
  }, [serializedData]);

  const lineOfCreditPaymentView = serializedData?.lineOfCreditPaymentView;
  const amountDue = lineOfCreditPaymentView?.prepopulatedPaymentAmount?.amount;
  const outstandingBalance =
    lineOfCreditPaymentView?.outstandingBalance?.amount;
  const paymentDueDate = lineOfCreditPaymentView?.dueDate ?? '';

  const [isDisabled, setIsDisabled] = useState(false);
  const [selectedSourceAccount, setSelectedSourceAccount] =
    useState<AccountDetailForSelector>();
  const [limitErrorString, setLimitErrorString] = useState<string>();
  const [transferAmount, setTransferAmount] = useState('0.00');
  const [paymentAlert, setPaymentAlert] = useState<PaymentAlert>({
    message: '',
    type: InlineAlertType.Info,
  });

  useEffect(() => {
    const { checkingAccount, savingsAccount } = {
      checkingAccount: getCheckingAccount(paymentAccountOptions),
      savingsAccount: getSavingAccount(paymentAccountOptions),
    };

    const [checkingAccountBalance, savingAccountBalance] = [
      getAvailableBalance(checkingAccount),
      getAvailableBalance(savingsAccount),
    ];

    const checkingBalancePositive =
      parseFloat(String(checkingAccountBalance)) > 0;
    const savingsBalancePositive = parseFloat(String(savingAccountBalance)) > 0;
    const selectedSourceAccount = () => {
      if (checkingBalancePositive) {
        return checkingAccount;
      }

      if (savingsBalancePositive) {
        return savingsAccount;
      }

      return checkingAccount;
    };

    setSelectedSourceAccount(selectedSourceAccount);
    setTransferAmount(amountDue as string);
  }, [amountDue, paymentAccountOptions]);

  useEffect(() => {
    const accountBalance = parseToFloat(
      selectedSourceAccount?.availableBalance?.amount,
    );
    const paymentInput = parseToFloat(transferAmount);
    const outstandingBalanceValue = parseToFloat(outstandingBalance);
    const amountDueValue = parseToFloat(amountDue);

    const isPaymentValid = paymentInput > 0;

    const notEnoughFunds = isPaymentValid && paymentInput > accountBalance;
    const moreThanAmountDue = isPaymentValid && paymentInput > amountDueValue;
    const moreThanOutstandingBalance =
      isPaymentValid && paymentInput > outstandingBalanceValue;
    const payingOff =
      isPaymentValid && paymentInput === outstandingBalanceValue;
    const pendingAmount = amountDueValue - paymentInput;

    setIsDisabled(
      notEnoughFunds || moreThanOutstandingBalance || accountBalance < 0,
    );

    const resetStates = () => {
      setPaymentAlert({
        message: '',
        type: InlineAlertType.Error,
      });
    };

    const updateLimitErrorString = (pendingAmount: number) => {
      resetStates();
      setLimitErrorString(
        translatedText('limitErrorStart', {
          pendingAmount: formatCurrency(pendingAmount),
          paymentDueDate:
            paymentDueDate && format(parseISO(paymentDueDate), 'MMMM dd, yyyy'),
        }),
      );
    };

    if (notEnoughFunds) {
      resetStates();
      setLimitErrorString('');
      setPaymentAlert({
        message: translatedText('notEnoughFunds'),
        type: InlineAlertType.Error,
      });
    } else if (payingOff) {
      resetStates();
      setLimitErrorString('');
      setPaymentAlert({
        message: translatedText('payingOff'),
        type: InlineAlertType.Info,
      });
    } else if (moreThanOutstandingBalance) {
      resetStates();
      setLimitErrorString('');
      setPaymentAlert({
        message: translatedText('moreThanOutstandingBalance'),
        type: InlineAlertType.Error,
      });
    } else if (moreThanAmountDue) {
      updateLimitErrorString(0.0);
      setPaymentAlert({
        message: translatedText('moreThanAmountDue'),
        type: InlineAlertType.Info,
      });
    } else if (pendingAmount > 0) {
      updateLimitErrorString(pendingAmount);
    } else {
      updateLimitErrorString(0.0);
    }
  }, [
    amountDue,
    paymentDueDate,
    selectedSourceAccount,
    transferAmount,
    translatedText,
    outstandingBalance,
  ]);

  const handleNumPadAction = (value: string) => {
    const userEnteredValue = applyKeyToAmount(transferAmount, value);
    setTransferAmount(userEnteredValue ?? transferAmount);
  };

  const handleAccountSelect = (account: AccountDetailForSelector) => {
    setSelectedSourceAccount(account);
    bottomSheetRef?.current?.dismiss();
  };

  const openBottomSheet = () => {
    bottomSheetRef?.current?.present();
  };

  const isNextDisabled = [
    isDisabled,
    !selectedSourceAccount,
    transferAmount === '0.00',
  ].some((condition) => condition);

  return {
    bottomSheetRef,
    handleNumPadAction,
    handleAccountSelect,
    isNextDisabled,
    limitErrorString,
    lineOfCreditPaymentView,
    openBottomSheet,
    paymentAccountOptions,
    paymentAlert,
    selectedSourceAccount,
    transferAmount,
    outstandingBalance,
    amountDue,
    loading: fetching,
    error,
  };
};

export default useRepayVaroLineOfCredit;
